import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import type { CreateEventBody } from '@ni/shared';
import { useCreateEvent } from '../api/event-hooks';
import { nameConflictOf } from '../api/errors';
import { NameConflict, type NameConflictWords } from '../components/name-conflict/name-conflict';
import { NoteLength, noteDescribedBy } from '../components/ui/char-count';
import { ErrorLine } from '../components/ui/error-line';
import { WorkingButton } from '../components/ui/working-button';
import {
  EVENTS_TITLE,
  EVENT_ABOUT_HELP,
  EVENT_ABOUT_LABEL,
  EVENT_CANCEL,
  EVENT_CONFLICT_LEAD,
  EVENT_CONFLICT_NEW,
  EVENT_CONFLICT_NEW_NOTE,
  EVENT_CONFLICT_TAKEN,
  EVENT_CREATE,
  EVENT_CREATING,
  EVENT_ENDS_LABEL,
  EVENT_NAME_LABEL,
  EVENT_NEW_TITLE,
  EVENT_PLACE_LABEL,
  EVENT_STARTS_LABEL,
} from '../lib/copy';
import { STATEMENT_LIMIT, statementOverBy } from '../lib/prose-limit';
import { useAccountForNewRecord } from './accounts';
import { Field } from '../components/ui/field';
import { TopBar } from '../components/ui/top-bar';

const EVENT_CONFLICT_WORDS: NameConflictWords = {
  lead: EVENT_CONFLICT_LEAD,
  newRecord: EVENT_CONFLICT_NEW,
  newRecordWorking: EVENT_CREATING,
  newRecordNote: EVENT_CONFLICT_NEW_NOTE,
  taken: EVENT_CONFLICT_TAKEN,
  recordPath: (entityId) => `/events/${entityId}`,
};

export interface EventFields {
  name: string;
  startsOn: string;
  endsOn: string;
  place: string;
  about: string;
  accountId?: string;
}

/** The form's fields as the create door reads them; blanks are nulls. */
export function eventBody(
  fields: EventFields,
  override: Partial<CreateEventBody> = {},
): CreateEventBody {
  const orNull = (value: string) => (value.trim().length > 0 ? value.trim() : null);
  return {
    name: fields.name.trim(),
    starts_on: orNull(fields.startsOn),
    ends_on: orNull(fields.endsOn),
    place: orNull(fields.place),
    about: orNull(fields.about),
    ...(fields.accountId ? { account_id: fields.accountId } : {}),
    ...override,
  };
}

/** Whether the dates as typed run backwards; the server refuses the same. */
export function datesBackwards(startsOn: string, endsOn: string): boolean {
  return Boolean(startsOn && endsOn && endsOn < startsOn);
}

/**
 * **New event** (docs/notes/2026-09-23-event-graph-design.md §3a). Creating an
 * event spends nothing; research starts from the event's page.
 */
export function NewEvent() {
  const navigate = useNavigate();
  const create = useCreateEvent();
  const [params] = useSearchParams();
  const account = useAccountForNewRecord(params.get('account_id'));

  const [fields, setFields] = useState<Omit<EventFields, 'accountId'>>({
    name: '',
    startsOn: '',
    endsOn: '',
    place: '',
    about: '',
  });
  const [deciding, setDeciding] = useState<string | null>(null);

  const over = statementOverBy(fields.about);
  const backwards = datesBackwards(fields.startsOn, fields.endsOn);
  const held = over > 0 || backwards || account.missing;
  const canSubmit = fields.name.trim().length > 0 && !held;

  const file = (override: Partial<CreateEventBody>) => {
    if (held || create.isPending) {
      setDeciding(null);
      return;
    }
    create.mutate(eventBody({ ...fields, accountId: account.accountId }, override), {
      onSuccess: (record) => navigate(`/events/${record.subject.entity_id}`),
      onSettled: () => setDeciding(null),
    });
  };

  const set = (key: keyof typeof fields) => (value: string) => {
    setFields((prev) => ({ ...prev, [key]: value }));
    if (create.isError) create.reset();
  };

  const conflict = nameConflictOf(create.error);
  const busy = create.isPending;

  return (
    <div className="column">
      <TopBar parent={{ to: '/events', label: EVENTS_TITLE }} here={EVENT_NEW_TITLE} />

      <div className="head">
        <div className="titles">
          <div className="title">{EVENT_NEW_TITLE}</div>
        </div>
      </div>

      <div className="form">
        <Field label={EVENT_NAME_LABEL} htmlFor="ne-name">
          <input
            id="ne-name"
            type="text"
            autoComplete="off"
            value={fields.name}
            disabled={busy}
            onChange={(event) => set('name')(event.target.value)}
          />
        </Field>

        {account.control}

        <div className="field-row">
          <Field label={EVENT_STARTS_LABEL} htmlFor="ne-starts">
            <input
              id="ne-starts"
              type="date"
              value={fields.startsOn}
              disabled={busy}
              onChange={(event) => set('startsOn')(event.target.value)}
            />
          </Field>
          <div className={backwards ? 'field over' : 'field'}>
            <label className="label" htmlFor="ne-ends">
              {EVENT_ENDS_LABEL}
            </label>
            <input
              id="ne-ends"
              type="date"
              value={fields.endsOn}
              disabled={busy}
              aria-invalid={backwards || undefined}
              onChange={(event) => set('endsOn')(event.target.value)}
            />
          </div>
        </div>

        <Field label={EVENT_PLACE_LABEL} htmlFor="ne-place">
          <input
            id="ne-place"
            type="text"
            value={fields.place}
            disabled={busy}
            onChange={(event) => set('place')(event.target.value)}
          />
        </Field>

        <div className={over > 0 ? 'field over' : 'field'}>
          <label className="label" htmlFor="ne-about">
            {EVENT_ABOUT_LABEL}
          </label>
          <textarea
            id="ne-about"
            rows={4}
            value={fields.about}
            disabled={busy}
            aria-invalid={over > 0 || undefined}
            aria-describedby={noteDescribedBy('ne-about', fields.about, STATEMENT_LIMIT)}
            onChange={(event) => set('about')(event.target.value)}
          />
          <NoteLength id="ne-about" value={fields.about} limit={STATEMENT_LIMIT} />
          <div className="field-note">{EVENT_ABOUT_HELP}</div>
        </div>
      </div>

      {conflict ? (
        <NameConflict
          details={conflict}
          pending={busy ? deciding : null}
          held={held}
          words={EVENT_CONFLICT_WORDS}
          onUse={(entityId) => {
            setDeciding(entityId);
            file({ entity_id: entityId });
          }}
          onCreateNew={() => {
            setDeciding('new');
            file({ force_new: true });
          }}
        />
      ) : create.isError ? (
        <ErrorLine error={create.error} />
      ) : null}

      <div className="form-actions">
        {conflict ? null : (
          <WorkingButton
            label={EVENT_CREATE}
            workingLabel={EVENT_CREATING}
            working={busy}
            disabled={!canSubmit}
            onClick={() => file({})}
          />
        )}
        <Link className="btn act" to="/events">
          {EVENT_CANCEL}
        </Link>
      </div>
    </div>
  );
}
